import { Star as StarIcon } from "@mui/icons-material";
import Logger from "../../services/Logger";
import { Box, Button, Chip, Typography } from "@mui/material";
import React from "react";
import AuthService from "../../services/AuthService";
import { usePagesTranslation } from "../locales";

const signUpButtonSx = {
    backgroundColor: "#ff9800",
    minHeight: 44,
    px: 2.5,
    py: 1,
    fontWeight: 700,
    textTransform: "none",
    boxShadow: "0 6px 20px rgba(255, 152, 0, 0.35)",
    transition: "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
    "&:hover": {
        backgroundColor: "#e65100",
        boxShadow: "0 8px 24px rgba(230, 81, 0, 0.4)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "scale(0.98)",
    },
};

const FREE_CHIP_SX = {
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: 700,
    fontSize: "0.75rem",
    height: 26,
    letterSpacing: "0.06em",
};

const BANNER_SX = {
    backgroundColor: "#fff8f0",
    borderRadius: 3,
    border: "1px solid rgba(255, 152, 0, 0.22)",
    px: { xs: 2.5, sm: 4, md: 5 },
    py: { xs: 3, sm: 4 },
    textAlign: "center",
    maxWidth: 720,
    mx: "auto",
    width: "100%",
};

function SignUpHeadline({ headline, freeLabel, titleVariant = "h4" }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1.5,
            }}
        >
            <Typography
                id="landing-signup-heading"
                variant={titleVariant}
                component="h2"
                sx={{
                    fontWeight: 700,
                    color: "#bf360c",
                    lineHeight: 1.25,
                    fontSize: titleVariant === "h4"
                        ? { xs: "1.35rem", sm: "1.65rem", md: "1.75rem" }
                        : { xs: "1.2rem", sm: "1.35rem" },
                }}
            >
                {headline}
            </Typography>
            <Chip label={freeLabel} size="small" sx={FREE_CHIP_SX} />
        </Box>
    );
}

const SignUpSection = ({ compact = false }) => {
    const { t } = usePagesTranslation();
    const headline = t(
        "landing.signup-headline",
        "Save Your Progress & Play More Puzzles",
    );
    const description = t(
        "landing.signup-description",
        "Create a free account to track your progress, save favorite puzzles, and unlock more games to play anytime.",
    );
    const freeLabel = t("core.signup.free", "FREE");
    const ctaLabel = t("landing.signup-cta", "Create Free Account");

    const handleSignUp = async () => {
        try {
            await AuthService.signup();
        } catch (error) {
            Logger.error("Error redirecting to signup:", error);
        }
    };

    if (compact) {
        return (
            <Box sx={{ ...BANNER_SX, mt: 3, mb: 0 }}>
                <SignUpHeadline headline={headline} freeLabel={freeLabel} titleVariant="h5" />
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2.5, maxWidth: 520, mx: "auto", lineHeight: 1.65 }}
                >
                    {description}
                </Typography>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleSignUp}
                    sx={signUpButtonSx}
                    startIcon={<StarIcon sx={{ fontSize: 18 }} />}
                >
                    {ctaLabel}
                </Button>
            </Box>
        );
    }

    return (
        <Box component="section" aria-labelledby="landing-signup-heading" sx={{ ...BANNER_SX, mb: 6 }}>
            <SignUpHeadline headline={headline} freeLabel={freeLabel} />
            <Typography
                component="p"
                variant="body1"
                color="text.secondary"
                sx={{
                    mb: 3,
                    maxWidth: 540,
                    mx: "auto",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                }}
            >
                {description}
            </Typography>
            <Button
                variant="contained"
                size="medium"
                onClick={handleSignUp}
                sx={signUpButtonSx}
                startIcon={<StarIcon sx={{ fontSize: 18 }} />}
            >
                {ctaLabel}
            </Button>
        </Box>
    );
};

export default SignUpSection;
